import { TbKey, TbPlugConnected } from "solid-icons/tb";
import { type Accessor, type Component, createEffect, createSignal, For, on, onCleanup, Show, untrack } from "solid-js";
import { AGENTS } from "~/components/marketing/content";
import { agentHandoffDemo } from "~/components/marketing/demo";
import { LogoStack } from "~/components/marketing/LogoMark";
import { AgentHandoffMockup } from "~/components/marketing/mockups";
import { Eyebrow, TextLink } from "~/components/marketing/ui";
import { type Scene, useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import "../home.css";
import type { TranslationKey } from "~/i18n/translations";

const MORE_AGENTS = AGENTS.filter((agent) =>
  [
    "OpenCode",
    "GitHub Copilot",
    "Cline",
    "Goose",
    "Junie",
    "Kiro CLI",
    "Qwen Code",
    "Factory Droid",
    "Augment",
    "OpenHands",
  ].includes(agent.name)
);

const CHIPS: { icon: typeof TbKey; title: TranslationKey; body: TranslationKey }[] = [
  { icon: TbPlugConnected, title: "marketing.home.builders.mcp_title", body: "marketing.home.builders.mcp_body" },
  { icon: TbKey, title: "marketing.home.builders.byok_title", body: "marketing.home.builders.byok_body" },
];

// Stands in for the agent while the sentence is split around it, so a
// translation keeps its own word order.
const NAME_MARK = "⁣";

const CARET = "ms-[0.04em] inline-block h-[0.8em] w-[0.07em] translate-y-[0.08em] bg-accent";

const TYPE_MS = 90;
const ERASE_MS = 45;

/**
 * Retypes the shown text whenever `target` changes: erases back to the part the
 * two names share, then types the rest. Unarmed (server render, reduced motion)
 * it swaps to the new name at once.
 */
const useTypewriter = (target: Accessor<string>, scene: Scene): Accessor<string> => {
  const [text, setText] = createSignal(untrack(target));
  let timer: number | undefined;
  createEffect(
    on(
      target,
      (name) => {
        window.clearTimeout(timer);
        if (!scene.armed()) return setText(name);
        const keystroke = () => {
          const now = text();
          if (!name.startsWith(now)) {
            setText(now.slice(0, -1));
            timer = window.setTimeout(keystroke, ERASE_MS);
          } else if (now.length < name.length) {
            setText(name.slice(0, now.length + 1));
            timer = window.setTimeout(keystroke, TYPE_MS);
          }
        };
        keystroke();
      },
      { defer: true }
    )
  );
  onCleanup(() => clearTimeout(timer));
  return text;
};

/**
 * The section's two-line headline, typing out the agent the mockup beside it
 * has open. Every name also sits invisibly in the same cell, so the line keeps
 * the widest one's room and nothing around it moves. Assistive tech reads one
 * stable sentence naming all the agents instead of the keystrokes.
 */
const AgentTitle: Component<{ names: string[]; current: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  const typed = useTypewriter(() => props.current, scene);
  const lines = () => t("marketing.home.builders.title", { agent: NAME_MARK }).split("\n");
  const parts = () => (lines()[1] ?? "").split(NAME_MARK);
  const spoken = () =>
    t("marketing.home.builders.title", {
      agent: new Intl.ListFormat(currentLanguage(), { type: "disjunction" }).format(props.names),
    });
  return (
    <h2
      ref={scene.ref}
      id="home-builders"
      class="scroll-mt-28 text-3xl font-semibold tracking-tight text-balance sm:text-4xl lg:text-5xl"
    >
      <span class="sr-only">{spoken()}</span>
      <span aria-hidden="true">{lines()[0]}</span>
      <Show when={lines()[1]}>
        <span aria-hidden="true" class="block text-secondary-text">
          {/* A translation without the placeholder yet reads as written. */}
          <Show when={parts().length === 2} fallback={lines()[1]}>
            {parts()[0]}
            <span class="inline-grid">
              <For each={props.names}>
                {(name) => (
                  <span class="invisible col-start-1 row-start-1 whitespace-nowrap">
                    {name}
                    <span class={CARET} />
                    {parts()[1]}
                  </span>
                )}
              </For>
              <span class="col-start-1 row-start-1 whitespace-nowrap">
                {typed()}
                <span class={`tw-caret ${CARET}`} classList={{ invisible: !scene.armed() }} />
                {parts()[1]}
              </span>
            </span>
          </Show>
        </span>
      </Show>
    </h2>
  );
};

/**
 * The page's one dark section. It pins the dark theme on itself and sits on a
 * deep navy, so it stays distinct from the canvas in either page theme.
 */
export const BuildersSection = () => {
  const { t } = useI18n();
  const demo = () => agentHandoffDemo(t);
  const [agent, setAgent] = createSignal(demo().agents[0].name);
  return (
    <section
      aria-labelledby="home-builders"
      class="dark relative overflow-hidden bg-marketing-navy px-4 py-20 text-primary-text sm:px-6 sm:py-28 lg:px-8"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -inset-e-40 -top-40 size-160 rounded-full bg-brand/25 blur-3xl"
      />
      <div class="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
        <div class="flex flex-col gap-5">
          <Eyebrow index="04" label={t("marketing.home.builders.eyebrow")} id="home-builders" class="text-accent" />
          <AgentTitle names={demo().agents.map((a) => a.name)} current={agent()} />
          <p class="max-w-xl text-lg leading-relaxed text-secondary-text">{t("marketing.home.builders.body")}</p>
          <ul class="grid gap-3 sm:grid-cols-2">
            <For each={CHIPS}>
              {(chip) => (
                <li class="rounded-xl border border-white/10 bg-white/5 p-4">
                  <p class="flex items-center gap-2 text-sm font-semibold">
                    <chip.icon class="size-4 text-accent" aria-hidden="true" />
                    {t(chip.title)}
                  </p>
                  <p class="mt-1.5 text-sm leading-relaxed text-secondary-text">{t(chip.body)}</p>
                </li>
              )}
            </For>
          </ul>
          <TextLink
            href="/for-builders"
            label={t("marketing.nav.for_builders")}
            describedBy="home-builders"
            class="text-accent"
          />
        </div>
        <div class="flex min-w-0 flex-col gap-4">
          <AgentHandoffMockup data={demo()} onAgentChange={(picked) => setAgent(picked.name)} />
          <p class="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-secondary-text">
            <LogoStack logos={MORE_AGENTS} />
            <span>
              {t("marketing.home.builders.any_agent", { count: AGENTS.length - 3 })}
              <span class="sr-only">: {MORE_AGENTS.map((more) => more.name).join(", ")}</span>
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};
